/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, signal,
} from '@angular/core';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { ArticleService } from '../services/article.service';
import { ConfirmActionDialogComponent } from '../common/community/feed/actions/confirm-action-dialog.component';

type SaveAction = 'autosave' | 'save' | 'review' | 'publish';
type ArticleField = 'title' | 'content' | 'slug';

const FIELD_IDS: Record<ArticleField, string> = {
  title: 'articleTitle',
  content: 'articleContent',
  slug: 'articleSlug',
};

const AUTOSAVE_MS = 3000;
const MIN_WORDS = 250;

// /new/article and /update/article/:articleId in the 2026 design: the writing surface on the left and a
// Publishing card on the right. Same rules as ut-article-form (required title, text and URL, 250 words for
// non-moderators, up to 2 tags, autosave every 3s until published, Submit for review or Publish by role), with
// errors inline instead of toasts. Saves run one after another, so an autosave can't race a submit into a
// duplicate article, and leaving with unsaved changes asks first.
@Component({
  selector: 'ut-article-form-page',
  templateUrl: './article-form-page.component.html',
  styleUrls: ['./article-form-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ArticleFormPageComponent implements OnInit, OnDestroy {
  // The state's `item` resolve ({ data: article }) and `user`.
  @Input({ required: true }) item: any;
  @Input() user: any;

  readonly fieldIds = FIELD_IDS;

  // How the page was opened. The first autosave of a new article gives it an id, which must not turn the page into
  // "Edit article" or drop the citing notice while the author is still writing.
  isNew = true;

  article: any;
  title = signal('');
  content = signal('');
  slug = signal('');
  availableTags = signal<any[]>([]);
  selectedTags = signal<any[]>([]);
  submitted = signal(false);
  saving = signal(false);
  serverError = signal('');
  slugServerError = signal('');
  autosaveOn = signal(true);
  autosave = signal<'idle' | 'saving' | 'saved' | 'error'>('idle');
  autosaveError = signal('');
  urlPre = signal('https://www.utest.com/articles/');
  dirty = false;
  // Bumped on every edit, so a save only clears `dirty` if nothing changed while it was in flight.
  private revision = 0;

  private queue: Promise<unknown> = Promise.resolve();
  private autosaveTimer: ReturnType<typeof setTimeout> | undefined;
  private removeLeaveHook: () => void;

  constructor(
    private articleService: ArticleService,
    private dialogService: DialogService,
    @Inject('StaticValuesService') private StaticValuesService: any,
    @Inject('ConfigService') private ConfigService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('ProgressBarService') private ProgressBarService: any,
    @Inject('ServerMessages') private ServerMessages: any,
    @Inject('$state') private $state: any,
    @Inject('$transitions') private $transitions: any,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.article = { ...this.item.data };
    this.isNew = !this.article.id;
    this.title.set(this.article.title || '');
    this.content.set(this.article.content || '');
    this.slug.set(this.article.slug || '');

    this.ConfigService.config().then((res) => {
      const base = res?.data?.utest_app_base_url || res?.data?.utest_url;
      if (base) this.urlPre.set(`${base}/articles/`);
    });
    this.StaticValuesService.tags().then((res) => {
      this.availableTags.set(res.data);
      this.selectedTags.set((this.article.tags || []).map((title) => res.data.find((t) => t.title === title)).filter(Boolean));
    });

    this.removeLeaveHook = this.$transitions.onBefore({}, () => (
      !this.dirty || window.confirm('You have unsaved changes. Leave this page anyway?')
    ));
  }

  ngOnDestroy(): void {
    clearTimeout(this.autosaveTimer);
    this.removeLeaveHook();
  }

  @HostListener('window:beforeunload', ['$event'])
  warnBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty) event.preventDefault();
  }

  get isAdmin(): boolean {
    return ['admin', 'moderator'].includes(this.user?.user_type);
  }

  get isPublished(): boolean {
    return this.article.state === 'published';
  }

  get canDelete(): boolean {
    return !!this.article.id && !!this.article.current_user_permissions?.destroy;
  }

  get statusLabel(): string {
    return {
      published: 'Published', review: 'Pending review', rejected: 'Rejected', archived: 'Archived',
    }[this.article.state] || 'Draft';
  }

  error(field: ArticleField): string {
    if (field === 'slug' && this.slugServerError()) return this.slugServerError();
    return this.submitted() ? this.validate()[field] || '' : '';
  }

  get errors(): { field: ArticleField; message: string }[] {
    return (Object.keys(FIELD_IDS) as ArticleField[])
      .map((field) => ({ field, message: this.error(field) }))
      .filter((e) => e.message);
  }

  focusField(field: ArticleField): void {
    const root = this.el.nativeElement;
    // The URL input only exists once there is a title to build it from, so until then its error points at the title.
    const target = root.querySelector<HTMLElement>(`#${FIELD_IDS[field]}`)
      ?? (field === 'slug' ? root.querySelector<HTMLElement>(`#${FIELD_IDS.title}`) : null);
    target?.focus();
  }

  setTitle(value: string): void {
    this.title.set(value);
    this.changed();
  }

  setContent(value: string): void {
    this.content.set(value);
    this.changed();
  }

  setSlug(value: string): void {
    this.slug.set(value);
    this.slugServerError.set('');
    this.changed();
  }

  setTags(tags: any[]): void {
    this.selectedTags.set(tags);
    this.changed();
  }

  toggleAutosave(): void {
    this.autosaveOn.set(!this.autosaveOn());
    clearTimeout(this.autosaveTimer);
    if (this.autosaveOn() && this.dirty && !this.isPublished) this.scheduleAutosave();
  }

  action(type: SaveAction): void {
    if (type !== 'autosave') {
      clearTimeout(this.autosaveTimer);
      if (this.saving()) return;
      this.submitted.set(true);
      this.serverError.set('');
    }
    const invalid = Object.values(this.validate());
    if (invalid.length) {
      if (type === 'autosave') {
        // Not autosave status: the form is just incomplete, so say nothing until the author submits.
        this.autosave.set('idle');
      } else {
        setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('.errsum')?.focus());
      }
      return;
    }
    if (type !== 'autosave') this.saving.set(true);
    this.queue = this.queue.then(() => this.persist(type));
  }

  confirmDelete(): void {
    this.dialogService.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Delete this article?', text: 'This can\'t be undone.', confirmLabel: 'Delete', destructive: true,
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.articleService.remove(this.article.id).then(() => {
        this.dirty = false;
        this.$state.go('article');
        this.AlertService.addSuccess('Article deleted.');
      }, (response) => this.AlertService.addDanger(this.ServerMessages.prettyMessage(response)));
    });
  }

  // Field problems, by field. Empty when the article can be saved.
  private validate(): Partial<Record<ArticleField, string>> {
    const result: Partial<Record<ArticleField, string>> = {};
    const text = this.content().trim();
    if (!this.title().trim()) result.title = 'Add a title.';
    if (!text) {
      result.content = 'Add the article text.';
    } else if (!this.isAdmin && text.split(' ').length < MIN_WORDS) {
      result.content = `Article must not contain fewer than ${MIN_WORDS} words.`;
    }
    if (!this.slug()) result.slug = 'Add a URL.';
    return result;
  }

  private changed(): void {
    this.dirty = true;
    this.revision += 1;
    if (this.autosaveOn() && !this.isPublished) this.scheduleAutosave();
  }

  private scheduleAutosave(): void {
    clearTimeout(this.autosaveTimer);
    this.autosave.set('idle');
    this.autosaveTimer = setTimeout(() => this.action('autosave'), AUTOSAVE_MS);
  }

  private persist(type: SaveAction): Promise<void> {
    const autosave = type === 'autosave';
    const { revision } = this;
    Object.assign(this.article, {
      title: this.title(), content: this.content(), slug: this.slug(), tags: this.selectedTags().map((t) => t.title),
    });
    if (autosave) {
      this.autosave.set('saving');
    } else {
      this.ProgressBarService.start();
    }
    const request = this.article.id ? this.articleService.update(this.article) : this.articleService.create(this.article);
    return request.then((res) => {
      this.article.id = res.data.id;
      if (this.revision === revision) this.dirty = false;
      if (autosave) {
        // Saving a rejected article turns it back into a draft, as before. "Saved" waits for that, so the
        // status badge (OnPush) refreshes with the new state and a failed draft() isn't shown as saved first.
        if (this.article.state === 'rejected') {
          return this.articleService.draft(this.article.id).then((draft) => {
            this.article = draft.data;
            this.autosave.set('saved');
          });
        }
        this.autosave.set('saved');
        return undefined;
      }
      if (type === 'publish' || type === 'review') {
        const next = type === 'publish' ? this.articleService.publish(this.article.id) : this.articleService.review(this.article.id);
        return next.then(() => {
          this.AlertService.add(type === 'publish' ? 'Article successfully published' : 'Article successfully submitted for review');
          this.$state.go('singlearticle', { articleId: this.article.slug });
        });
      }
      this.AlertService.add('Article successfully saved');
      if (res.data.state === 'published') this.$state.go('singlearticle', { articleId: res.data.slug });
      return undefined;
    }).catch((response) => this.showError(response, autosave)).finally(() => {
      if (!autosave) {
        this.saving.set(false);
        this.ProgressBarService.done();
      }
    });
  }

  private showError(response: any, autosave: boolean): void {
    const error = typeof response?.data === 'string' ? response.data : response?.data?.errors?.[0] || {};
    let message: string;
    if (error.source?.parameter === 'slug') {
      if (error.title === 'already exists') {
        message = 'An article with this title already exists';
      } else if (/^[0-9]+$/.test(this.slug())) {
        message = 'Slug can\'t only contain numbers.';
      } else {
        message = 'Slug can only contain letters, numbers, dashes and underscores.';
      }
      if (!autosave) this.slugServerError.set(message);
    } else {
      message = this.ServerMessages.prettyMessage(response);
      if (!autosave) this.serverError.set(message);
    }
    if (autosave) {
      this.autosave.set('error');
      this.autosaveError.set(message);
    } else {
      setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('.errsum')?.focus());
    }
  }
}
